/**
 * Interaction capture for the telemetry `interaction` group: what the
 * user clicked, toggled, chose and pressed, how they scrolled, and the viewport.
 *
 * - Passive capture-phase listeners on `document` (and `window` for resize / focus);
 *   started and stopped by services/telemetry.js with the group, idempotent under
 *   StrictMode's double effects.
 * - Target identity: the nearest identified region (a `[data-ui]` control id, a
 *   `[data-surface]`, each with its `-key` as `name[key]`, then a `[data-testid]`, an
 *   `#id`, a landmark role or an aria-label; never text content) with a `path` of up to three,
 *   and for a role-bearing element its accessible name (aria-label → title → trimmed
 *   text for interactive roles only, 80 chars). A chart click is placed by its `<svg>`
 *   with `part` the shape clicked. Inside a `[data-telemetry-redact]` subtree the name,
 *   any aria-label and any value are never recorded, only that it was redacted.
 * - Never an input's value. Checkboxes, radios, selects, sliders and date inputs
 *   record the resulting state; typing in a text field is summarised on blur as a
 *   key count and a duration. `key.down` is non-printable keys only (Enter, Escape,
 *   Tab, arrows, …) and combinations with Ctrl / Meta / Alt.
 * - Scroll: at most one `scroll.container` per container every 500 ms, carrying the
 *   trailing position and how many scroll events it stands for.
 */
import { scrubFreeText } from "../lib/telemetry/sanitize.js";

export const SCROLL_INTERVAL_MS = 500;
const RESIZE_DEBOUNCE_MS = 250;
const NAME_MAX = 80;
export const REDACT_ATTR = "data-telemetry-redact";

/** Elements a click is attributed to, nearest first. */
const ACTIONABLE =
  "button, a, input, select, textarea, label, summary, [role], [data-ui], [data-testid], [tabindex]";
/** The registry's name rules (lib/telemetry/registry) and the id shape of their keys. */
const NAME_RE = {
  "data-ui": /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/,
  "data-surface": /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)*$/,
};
const INSTRUMENT_KEY_RE = /^[A-Za-z0-9._:-]{1,64}$/;
/** Input types whose value is the user's text; they never record a value. */
const TEXT_TYPES = new Set(["", "text", "search", "email", "url", "tel", "password", "number"]);
/** Inputs whose click is recorded through their change event instead. */
const STATE_TYPES = new Set([
  "checkbox",
  "radio",
  "range",
  "date",
  "datetime-local",
  "time",
  "month",
  "week",
  "color",
]);
/** Inputs whose resulting number is recorded as `value`. */
const NUMBER_TYPES = new Set(["range"]);
/** Inputs whose resulting ISO date / date-time is recorded as `date`. */
const DATE_TYPES = new Set(["date", "datetime-local"]);
/** Named keys recorded as `key.down` (besides F1–F24 and modifier combinations). */
const ACTION_KEYS = new Set([
  "Enter",
  "Escape",
  "Tab",
  "Backspace",
  "Delete",
  "Insert",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "CapsLock",
  "ContextMenu",
]);
/** Keys recorded inside a text field: the ones that act rather than type. */
const FIELD_ACTION_KEYS = new Set(["Enter", "Escape", "Tab"]);

const IMPLICIT_ROLES = {
  a: "link",
  button: "button",
  select: "combobox",
  textarea: "textbox",
  summary: "button",
  dialog: "dialog",
};

const tagOf = (el) => (el && el.tagName ? el.tagName.toLowerCase() : "");

function inputType(el) {
  return tagOf(el) === "input" ? (el.getAttribute("type") || "").toLowerCase() : null;
}

/** Whether `el` takes typed text (text-like input, textarea, contenteditable). */
export function isTextField(el) {
  if (!el || typeof el !== "object") return false;
  const tag = tagOf(el);
  if (tag === "textarea") return true;
  if (tag === "input") return TEXT_TYPES.has(inputType(el));
  return el.isContentEditable === true;
}

function roleOf(el) {
  const explicit = el.getAttribute?.("role");
  if (explicit) return explicit;
  const type = inputType(el);
  if (type !== null) {
    if (type === "checkbox" || type === "radio") return type;
    if (type === "range") return "slider";
    if (TEXT_TYPES.has(type)) return "textbox";
    return "input";
  }
  return IMPLICIT_ROLES[tagOf(el)] || null;
}

/** Roles whose visible text is a control's label, not content. */
const INTERACTIVE_ROLES = new Set([
  "button",
  "link",
  "checkbox",
  "radio",
  "switch",
  "tab",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "option",
  "slider",
  "combobox",
  "spinbutton",
  "treeitem",
]);
/** Structural roles that name a region of the page. */
const LANDMARK_ROLES = new Set([
  "region",
  "dialog",
  "alertdialog",
  "navigation",
  "main",
  "toolbar",
  "tablist",
  "listbox",
  "grid",
  "menu",
  "tree",
  "complementary",
  "banner",
  "form",
  "search",
]);
const LANDMARK_TAGS = { dialog: "dialog", nav: "navigation", main: "main" };
/** SVG elements a chart click lands on; they are identified by their owning `<svg>`. */
const SVG_PARTS = new Set([
  "svg",
  "line",
  "path",
  "rect",
  "circle",
  "g",
  "text",
  "polyline",
  "polygon",
  "ellipse",
]);
const PATH_HOPS = 3;

/**
 * aria-label → title, and the visible text only for an interactive role (a button's
 * label). Never a text field's contents, never a non-interactive element's text.
 */
function accessibleName(el, role) {
  const attr = el.getAttribute?.("aria-label") || el.getAttribute?.("title");
  const text = attr || (INTERACTIVE_ROLES.has(role) && !isTextField(el) ? el.textContent : "");
  const clean = (text || "").replace(/\s+/g, " ").trim();
  return clean ? scrubFreeText(clean, NAME_MAX) : null;
}

/**
 * `name` or `name[key]` from an instrumentation attribute pair (`data-ui` + `data-ui-key`,
 * `data-surface` + `data-surface-key`). Both are developer-set ids, not text, so they are
 * kept verbatim when they have the registry's shape; a malformed name is ignored and a
 * malformed key dropped.
 */
function instrumentIdent(node, attr) {
  const name = node.getAttribute?.(attr);
  if (!name || !NAME_RE[attr].test(name)) return null;
  const key = node.getAttribute?.(`${attr}-key`);
  return key && INSTRUMENT_KEY_RE.test(key) ? `${name}[${key}]` : name;
}

/**
 * How an ancestor names a region: its `data-ui` control id, its `data-surface`, then
 * `data-testid`, `#id`, a landmark `role` (with its aria-label, unless redacted), or
 * `label:<aria-label>`. Never its text content.
 */
function regionIdent(node, redacted) {
  const instrumented = instrumentIdent(node, "data-ui") || instrumentIdent(node, "data-surface");
  if (instrumented) return instrumented;
  const testid = node.getAttribute?.("data-testid");
  if (testid) return testid;
  const id = node.getAttribute?.("id");
  // The React mount point contains everything; it identifies nothing.
  if (id && id !== "root") return scrubFreeText(`#${id}`, NAME_MAX);
  const role = node.getAttribute?.("role") || LANDMARK_TAGS[tagOf(node)];
  const label = redacted ? null : node.getAttribute?.("aria-label");
  if (role && LANDMARK_ROLES.has(role)) {
    return scrubFreeText(label ? `${role}:${label}` : role, NAME_MAX);
  }
  return label ? scrubFreeText(`label:${label}`, NAME_MAX) : null;
}

/**
 * `{target, role, name, tag, part, path, redacted}` for `el` (see the file header):
 * `target` is the nearest identified region, `path` up to three of them outermost
 * first; a click inside a chart is placed by its owning `<svg>` with `part` the shape,
 * unless a shape inside the svg carries its own `data-ui`.
 */
export function describeTarget(el) {
  if (!el || typeof el !== "object" || !el.tagName) return { target: null, tag: null };
  const redacted = !!el.closest?.(`[${REDACT_ATTR}]`);
  let base = el;
  const out = { target: null, tag: tagOf(el) };
  if (SVG_PARTS.has(out.tag)) {
    const svg = out.tag === "svg" ? el : el.closest?.("svg");
    if (svg) {
      let tagged = null;
      for (let n = el; n && n !== svg && !tagged; n = n.parentElement) {
        if (instrumentIdent(n, "data-ui")) tagged = n;
      }
      base = tagged || svg;
      if (svg !== el) out.part = out.tag;
      out.tag = "svg";
    }
  }
  const idents = [];
  for (let n = base; n && idents.length < PATH_HOPS; n = n.parentElement) {
    const ident = n.nodeType === 1 ? regionIdent(n, redacted) : null;
    if (ident) idents.push(ident);
  }
  if (idents.length) {
    out.target = idents[0];
    out.path = idents.slice().reverse().join(" > ");
  }
  const role = roleOf(el);
  if (role) out.role = role;
  if (redacted) out.redacted = true;
  else if (role) out.name = accessibleName(el, role);
  return out;
}

export const elementOf = (t) => (t && t.nodeType === 1 ? t : t && t.parentElement) || null;

/**
 * The element `action.click` describes for a click on `el`: its nearest actionable
 * ancestor, or — a chart shape whose nearest actionable ancestor is only a region (a
 * testid'd chart container) — the shape itself, so its svg `part` survives; an icon inside
 * a real control still reports the control.
 */
export function clickedElement(el) {
  const hit = el.closest?.(ACTIONABLE) || el;
  return SVG_PARTS.has(tagOf(el)) && !roleOf(hit) ? el : hit;
}

/** Whether `el` is a control whose click is recorded through its change event. */
export function isStateControl(el) {
  return STATE_TYPES.has(inputType(el)) || tagOf(el) === "select";
}

/**
 * The nearest well-formed `data-ui` / `data-surface` around `el`: `{name, key}` (key null
 * when absent or not id-shaped), or null.
 */
export function nearestInstrument(el, attr) {
  for (let n = el; n; n = n.parentElement) {
    if (n.nodeType !== 1) continue;
    const name = n.getAttribute?.(attr);
    if (!name || !NAME_RE[attr].test(name)) continue;
    const key = n.getAttribute?.(`${attr}-key`);
    return { name, key: key && INSTRUMENT_KEY_RE.test(key) ? key : null };
  }
  return null;
}

/** Create the capture; `record(event)` receives each event (services/telemetry.js). */
export function createInteractionCapture({
  record,
  doc = typeof document !== "undefined" ? document : null,
  win = typeof window !== "undefined" ? window : null,
  now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl = (id) => clearTimeout(id),
} = {}) {
  let started = false;
  let listeners = [];
  /** Per scrolled container: the pending coalesced run. */
  let scrolls = new WeakMap();
  /** Pending trailing-scroll timers, cleared on stop. */
  let scrollTimers = new Set();
  /** The text field being typed in: `{el, keys, start}`. */
  let typing = null;
  let resizeTimer = null;

  function emit(event) {
    try {
      record(event);
    } catch {
      /* never surface */
    }
  }

  function listen(target, type, fn, capture = true) {
    if (!target) return;
    target.addEventListener(type, fn, { capture, passive: true });
    listeners.push([target, type, fn, capture]);
  }

  function onClick(e) {
    const el = elementOf(e.target);
    if (!el) return;
    const hit = el.closest?.(ACTIONABLE) || el;
    if (isStateControl(hit)) return;
    emit({ event: "action.click", ...describeTarget(clickedElement(el)) });
  }

  function onChange(e) {
    const el = elementOf(e.target);
    if (!el || isTextField(el)) return;
    const target = describeTarget(el);
    const type = inputType(el);
    if (type === "checkbox" || type === "radio") {
      emit({
        event: "action.toggle",
        ...target,
        ...(target.redacted ? {} : { checked: el.checked === true }),
      });
    } else if (tagOf(el) === "select") {
      emit({
        event: "action.change",
        ...target,
        ...(target.redacted ? {} : { selected: scrubFreeText(el.value, NAME_MAX) }),
      });
    } else if (NUMBER_TYPES.has(type)) {
      const n = Number(el.value);
      emit({
        event: "action.change",
        ...target,
        ...(target.redacted || !Number.isFinite(n) ? {} : { value: n }),
      });
    } else if (DATE_TYPES.has(type)) {
      emit({
        event: "action.change",
        ...target,
        ...(target.redacted || !el.value ? {} : { date: el.value }),
      });
    }
  }

  function onSubmit(e) {
    const el = elementOf(e.target);
    if (el) emit({ event: "action.submit", ...describeTarget(el) });
  }

  function endTyping() {
    if (!typing) return;
    const { el, keys, start } = typing;
    typing = null;
    emit({
      event: "key.typing",
      ...describeTarget(el),
      keys,
      duration_ms: Math.round(now() - start),
    });
  }

  function onKeyDown(e) {
    const key = e.key;
    if (typeof key !== "string" || key === "" || key === "Unidentified") return;
    const el = elementOf(e.target);
    // AltGr types a character and reports as Ctrl+Alt: that is typing, not a shortcut.
    const altGraph = e.getModifierState?.("AltGraph") === true || (e.ctrlKey && e.altKey);
    const mod = (e.ctrlKey || e.metaKey || e.altKey) && !(altGraph && key.length === 1);
    const printable = key.length === 1;
    if (isTextField(el) && !mod && !FIELD_ACTION_KEYS.has(key)) {
      if (!typing || typing.el !== el) {
        endTyping();
        typing = { el, keys: 0, start: now() };
      }
      typing.keys += 1;
      return;
    }
    if (printable && !mod) return;
    // Named keys the server keeps; anything else (media, NumLock, …) is not recorded.
    if (!printable && !ACTION_KEYS.has(key) && !/^F([1-9]|1\d|2[0-4])$/.test(key)) return;
    emit({
      event: "key.down",
      ...describeTarget(el),
      key,
      ctrl: !!e.ctrlKey,
      meta: !!e.metaKey,
      alt: !!e.altKey,
      shift: !!e.shiftKey,
      repeat: !!e.repeat,
    });
  }

  function onFocusOut(e) {
    if (typing && elementOf(e.target) === typing.el) endTyping();
  }

  function scrollPosition(target) {
    if (target === doc || !target || target.nodeType === 9) {
      const se = doc?.scrollingElement || doc?.documentElement;
      return { x: se ? se.scrollLeft : 0, y: se ? se.scrollTop : 0, container: "document" };
    }
    // The nearest ancestor with a well-formed `data-ui` / `data-surface` or a `data-testid`
    // names the container (preferring the first two on the same element); a malformed name
    // is skipped, not taken as the end of the search.
    let container = null;
    for (let n = target; n && !container; n = n.parentElement) {
      if (n.nodeType !== 1) continue;
      container =
        instrumentIdent(n, "data-ui") ||
        instrumentIdent(n, "data-surface") ||
        n.getAttribute?.("data-testid") ||
        null;
    }
    return {
      x: target.scrollLeft,
      y: target.scrollTop,
      container: container || tagOf(target),
    };
  }

  function flushScroll(target, st) {
    if (st.timer !== null) scrollTimers.delete(st.timer);
    st.timer = null;
    if (st.count === 0) return;
    const { x, y, container } = scrollPosition(target);
    emit({
      event: "scroll.container",
      container,
      x,
      y,
      dx: x - st.x,
      dy: y - st.y,
      count: st.count,
      first_t_mono: st.first,
      last_t_mono: st.last,
    });
    st.x = x;
    st.y = y;
    st.count = 0;
    st.first = null;
    st.lastEmit = now();
  }

  function onScroll(e) {
    const target = e.target;
    if (!target) return;
    const t = now();
    let st = scrolls.get(target);
    if (!st) {
      // The run starts from where the container was before this event, unknown here.
      const { x, y } = scrollPosition(target);
      st = { x, y, count: 0, first: null, last: null, lastEmit: -Infinity, timer: null };
      scrolls.set(target, st);
    }
    st.count += 1;
    if (st.first === null) st.first = t;
    st.last = t;
    if (st.timer !== null) return;
    const wait = SCROLL_INTERVAL_MS - (t - st.lastEmit);
    if (wait <= 0) flushScroll(target, st);
    else {
      st.timer = setTimeoutImpl(() => flushScroll(target, st), wait);
      scrollTimers.add(st.timer);
    }
  }

  function onResize() {
    if (resizeTimer !== null) clearTimeoutImpl(resizeTimer);
    resizeTimer = setTimeoutImpl(() => {
      resizeTimer = null;
      emit({
        event: "viewport.resize",
        w: win.innerWidth,
        h: win.innerHeight,
        dpr: win.devicePixelRatio || 1,
      });
    }, RESIZE_DEBOUNCE_MS);
  }

  const onFocus = () => emit({ event: "viewport.focus", focused: true });
  const onBlur = () => emit({ event: "viewport.focus", focused: false });
  const onVisibility = () => emit({ event: "viewport.visibility", state: doc.visibilityState });

  return {
    /** Install the listeners; idempotent. */
    start() {
      if (started || !doc) return;
      started = true;
      listen(doc, "click", onClick);
      listen(doc, "change", onChange);
      listen(doc, "submit", onSubmit);
      listen(doc, "keydown", onKeyDown);
      listen(doc, "focusout", onFocusOut);
      listen(doc, "scroll", onScroll);
      listen(doc, "visibilitychange", onVisibility);
      // Bubble phase on window: element focus events do not bubble, so these see the
      // window's own focus changes only.
      listen(win, "resize", onResize, false);
      listen(win, "focus", onFocus, false);
      listen(win, "blur", onBlur, false);
    },
    /** Remove the listeners and drop pending runs; idempotent. */
    stop() {
      if (!started) return;
      started = false;
      for (const [target, type, fn, capture] of listeners) {
        target.removeEventListener(type, fn, { capture });
      }
      listeners = [];
      endTyping();
      for (const id of scrollTimers) clearTimeoutImpl(id);
      scrollTimers = new Set();
      scrolls = new WeakMap();
      if (resizeTimer !== null) clearTimeoutImpl(resizeTimer);
      resizeTimer = null;
    },
    get started() {
      return started;
    },
  };
}
